import { crmApi, CrmOption } from '../../services/api/crmApi';

export type CrmSourceKey = 'segment' | 'channel' | 'lead';

/** Filter values are plain strings so they can be stored on a form field as-is. */
export type CrmFilters = Record<string, string>;

export interface CrmFilterDef {
  key: string;
  label: string;
  placeholder?: string;
  help?: string;
}

export interface CrmSourceDef {
  key: CrmSourceKey;
  label: string;
  /** Lower-case plural, used in empty/error copy. */
  noun: string;
  /**
   * Too large to list at once: the CRM is queried as the visitor types
   * (server-side, see `search`) instead of filtering one loaded list.
   */
  searchable?: boolean;
  filters: CrmFilterDef[];
  fetch: (tenantId: string, filters: CrmFilters) => Promise<CrmOption[]>;
  /** Searchable sources only: the query the visitor has typed so far. */
  search?: (tenantId: string, query: string, filters: CrmFilters) => Promise<CrmOption[]>;
}

/**
 * Everything a CRM autocomplete can list. The component and the field's edit
 * panel are both driven from this table, so adding a source (funnel, user, ...)
 * is one entry here plus one backend endpoint.
 */
export const CRM_SOURCES: Record<CrmSourceKey, CrmSourceDef> = {
  segment: {
    key: 'segment',
    label: 'Segment',
    noun: 'segments',
    filters: [],
    fetch: (tenantId) => crmApi.getSegments(tenantId),
  },
  channel: {
    key: 'channel',
    label: 'Channel',
    noun: 'channels',
    filters: [
      {
        key: 'type',
        label: 'Channel type',
        placeholder: 'e.g. SMTP',
        help: 'Only list active channels of this type. Leave empty for all channels.',
      },
    ],
    fetch: (tenantId, filters) => crmApi.getChannels(tenantId, filters.type),
  },
  lead: {
    key: 'lead',
    label: 'Lead',
    noun: 'leads',
    searchable: true,
    filters: [],
    // Seeds the dropdown with the CRM's default list before the visitor types.
    fetch: (tenantId) => crmApi.getLeads(tenantId),
    search: (tenantId, query) => crmApi.getLeads(tenantId, query),
  },
};

export const CRM_SOURCE_LIST = Object.values(CRM_SOURCES);

export const getCrmSource = (key?: string): CrmSourceDef =>
  CRM_SOURCES[(key as CrmSourceKey) || 'segment'] || CRM_SOURCES.segment;
